import {useGetPatientsActiveSubstanceTotals} from '@/modules/patients/api/use-get-patient-active-substance-totals'
import {useGetPatientProtocols} from '@/modules/patients/api/use-get-patient-protocols'
import {useGetPatientTherapies} from '@/modules/patients/api/use-get-patient-therapies'
import {useGetPatientCharacteristicsHistory} from '@/modules/patients/api/use-get-patient-characteristics-history'
import {useGetPatientGfrLabs} from '@/modules/patients/api/use-get-patient-gfr-labs'
import {OverdosePreventionLevel} from '@cypro/engine/database/schema'
import {PatientProtocol, PatientTherapy} from '@cypro/engine/queries'
import {DrugDetails} from '@cypro/engine/utils'
import {useQueryClient} from '@tanstack/react-query'

import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
  Dispatch,
  SetStateAction,
} from 'react'
import {useGetPatientsCumulatedDose} from '@/modules/patients/api'
import {pruneSelection, toggleRowSelection, toggleSelectionGroup} from '@/modules/patients/therapy-selection'
export interface PreviousPrescription {
  amount: number
  date: number
}

export interface PreviousProtocolPrescription {
  amount: number
  day: number
}

export interface DrugLimitCheckResult {
  level: string
  limit: number
  substanceName: string
  substanceId: string
  overdosePreventionLevel?: OverdosePreventionLevel
  unit: string
}

interface State {
  patientId: string
  patientProtocols: PatientProtocol[]
  patientTherapies: PatientTherapy[]
  filteredPatientTherapies: PatientTherapy[]

  selectedProtocolIdsState: string[]
  setSelectedProtocolIdsState: Dispatch<SetStateAction<string[]>>
  patientDrugAmountHistory?: Record<string, DrugDetails>
  cumulatedDose?: Record<string, DrugDetails>
  characteristicsHistory: readonly {createdAt: number; weight: number | null; bsa: number | null}[]
  gfrHistory: readonly {labTime: number; gfr: number}[]
  hideIsolated: boolean
  hidePrevious: boolean
  hideNotes: boolean
  setPrescriptionsVisibility: (r: boolean) => void
  setPrescriptionsPreviousVisibility: (r: boolean) => void
  setHideNotes: (r: boolean) => void
  /** Patient arrived via HL7 and hasn't been imported yet; therapy can't be added or edited. */
  isPendingHl7Import: boolean
  selectedTherapyIds: ReadonlySet<string>
  selectedTherapyAnchorId: string | null
  toggleTherapySelection: (targetId: string, orderedIds: readonly string[], shift?: boolean) => void
  toggleTherapyGroup: (ids: readonly string[]) => void
  clearTherapySelection: () => void
  pruneTherapySelection: (selectableIds: readonly string[]) => void
}
interface Props extends PropsWithChildren {
  patientId: string
  isPendingHl7Import?: boolean
}

const PatientTherapyContext = createContext<State>({} as State)

export function PatientTherapyProvider({children, patientId, isPendingHl7Import = false}: Props) {
  // Get patient protocols
  const [patientProtocols] = useGetPatientProtocols(patientId)

  const queryClient = useQueryClient()

  // To Change
  const [selectedProtocolIdsState, setSelectedProtocolIdsState] = useState<string[]>([])
  const [hideIsolated, setHideIsolated] = useState(false)
  const [hidePrevious, setHidePrevious] = useState(true)
  const [hideNotes, setHideNotes] = useState(false)
  const [selectedTherapyIds, setSelectedTherapyIds] = useState<ReadonlySet<string>>(() => new Set())
  const [selectedTherapyAnchorId, setSelectedTherapyAnchorId] = useState<string | null>(null)

  const toggleTherapySelection = useCallback(
    (targetId: string, orderedIds: readonly string[], shift = false) => {
      const next = toggleRowSelection(selectedTherapyIds, selectedTherapyAnchorId, targetId, orderedIds, shift)
      setSelectedTherapyIds(next.ids)
      setSelectedTherapyAnchorId(next.anchorId)
    },
    [selectedTherapyIds, selectedTherapyAnchorId]
  )

  const toggleTherapyGroup = useCallback((ids: readonly string[]) => {
    setSelectedTherapyIds(prev => toggleSelectionGroup(prev, ids))
  }, [])

  const clearTherapySelection = useCallback(() => {
    setSelectedTherapyIds(new Set())
    setSelectedTherapyAnchorId(null)
  }, [])

  const pruneTherapySelection = useCallback((selectableIds: readonly string[]) => {
    setSelectedTherapyIds(prev => pruneSelection(prev, selectableIds))
  }, [])

  // Get patient therapies for drug limit calculations and history
  const [patientTherapies] = useGetPatientTherapies(patientId)
  const [filteredPatientTherapies] = useGetPatientTherapies(
    patientId,
    selectedProtocolIdsState,
    hideIsolated,
    hidePrevious
  )

  const {data: history} = useGetPatientsActiveSubstanceTotals({patientId})
  const {data: cumulatedDose} = useGetPatientsCumulatedDose({patientId})
  const [characteristicsHistory] = useGetPatientCharacteristicsHistory(patientId)
  const [gfrHistory] = useGetPatientGfrLabs(patientId)

  function setPrescriptionsVisibility(visibility: boolean) {
    setHideIsolated(visibility)
  }
  function setPrescriptionsPreviousVisibility(visibility: boolean) {
    setHidePrevious(visibility)
  }

  useEffect(() => {
    queryClient.invalidateQueries({queryKey: ['patients-total-active-substances']})
    queryClient.invalidateQueries({queryKey: ['patients-cumulated-dose']})
  }, [filteredPatientTherapies, queryClient])

  useEffect(() => {
    if (!patientProtocols || patientProtocols.length === 0) return
    setSelectedProtocolIdsState(prev => {
      if (prev.length > 0) return prev
      const now = new Date().getTime()
      // Active protocols have no end date; only those with an end date in the past are excluded.
      return patientProtocols.filter(p => p.endDate == null || now < p.endDate).map(p => p.id)
    })
  }, [patientProtocols, setSelectedProtocolIdsState])

  return (
    <PatientTherapyContext.Provider
      value={{
        patientId,
        patientTherapies: patientTherapies,
        patientProtocols: patientProtocols,
        filteredPatientTherapies,
        selectedProtocolIdsState,
        setSelectedProtocolIdsState,
        patientDrugAmountHistory: history as Record<string, DrugDetails>,
        cumulatedDose: cumulatedDose as Record<string, DrugDetails>,
        characteristicsHistory,
        gfrHistory,
        setPrescriptionsVisibility,
        setPrescriptionsPreviousVisibility,
        hidePrevious,
        hideIsolated,
        hideNotes,
        setHideNotes,
        isPendingHl7Import,
        selectedTherapyIds,
        selectedTherapyAnchorId,
        toggleTherapySelection,
        toggleTherapyGroup,
        clearTherapySelection,
        pruneTherapySelection,
      }}
    >
      {children}
    </PatientTherapyContext.Provider>
  )
}

export function usePatientTherapyContext() {
  return useContext(PatientTherapyContext)
}
